import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { brandApi, modelApi } from '../api/client';
import type { Model, BadgeType } from '@bosamie/shared';
import {
  showToast, ConfirmDialog, Drawer, BadgeChip,
  TableSkeleton, EmptyState, formatPrice,
} from '../components/ui';

export function Models() {
  const { brandId } = useParams<{ brandId: string }>();
  const navigate = useNavigate();
  const [brand, setBrand] = useState<{ name: string } | null>(null);
  const [models, setModels] = useState<(Model & { saleColorCount: number })[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);

  // Form state
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [price, setPrice] = useState(0);
  const [desc, setDesc] = useState('');
  const [badge, setBadge] = useState<BadgeType[]>([]);

  // Inline edit
  const [editingPriceId, setEditingPriceId] = useState<string | null>(null);
  const [editingPrice, setEditingPrice] = useState('');

  const [deleteTarget, setDeleteTarget] = useState<Model | null>(null);

  const fetch = useCallback(async () => {
    if (!brandId) return;
    setLoading(true);
    try {
      const [brandData, modelData] = await Promise.all([
        brandApi.list(),
        modelApi.list(brandId),
      ]);
      const b = brandData.find((x) => x.id === brandId);
      if (b) setBrand({ name: b.name });
      setModels(modelData);
    } catch {
      showToast('加载失败');
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  useEffect(() => { fetch(); }, [fetch]);

  const openCreate = () => {
    setEditId(null);
    setCode('');
    setName('');
    setPrice(0);
    setDesc('');
    setBadge([]);
    setDrawerOpen(true);
  };

  const openEdit = (model: Model) => {
    setEditId(model.id);
    setCode(model.code);
    setName(model.name);
    setPrice(model.price);
    setDesc(model.desc || '');
    setBadge(model.badge);
    setDrawerOpen(true);
  };

  const handleSave = async () => {
    if (!brandId) return;
    if (!code.trim()) { showToast('编码不能为空'); return; }
    if (!name.trim()) { showToast('名称不能为空'); return; }
    if (!Number.isInteger(price) || price < 0) { showToast('价格必须为非负整数'); return; }

    try {
      const data = { code: code.trim(), name: name.trim(), price, desc: desc || undefined, badge };
      if (editId) {
        await modelApi.update(brandId, editId, data);
        showToast('保存成功');
      } else {
        await modelApi.create(brandId, data);
        showToast('创建成功');
      }
      setDrawerOpen(false);
      fetch();
    } catch (e) {
      showToast(e instanceof Error ? e.message : '保存失败');
    }
  };

  const handleDelete = async () => {
    if (!brandId || !deleteTarget) return;
    try {
      await modelApi.delete(brandId, deleteTarget.id);
      showToast('删除成功');
      setDeleteTarget(null);
      fetch();
    } catch {
      showToast('删除失败');
    }
  };

  const submitPrice = async (modelId: string) => {
    const newPrice = Number(editingPrice);
    if (!Number.isInteger(newPrice) || newPrice < 0) {
      showToast('价格必须为非负整数');
      setEditingPriceId(null);
      return;
    }
    try {
      await modelApi.updatePrice(modelId, newPrice);
      setModels((prev) => prev.map((m) => m.id === modelId ? { ...m, price: newPrice } : m));
      showToast('改价成功');
    } catch {
      showToast('改价失败');
    }
    setEditingPriceId(null);
  };

  const toggleBadge = async (model: Model, type: BadgeType) => {
    const newBadge = model.badge.includes(type)
      ? model.badge.filter((b) => b !== type)
      : [...model.badge, type];
    try {
      await modelApi.updateBadge(model.id, newBadge);
      setModels((prev) => prev.map((m) => m.id === model.id ? { ...m, badge: newBadge } : m));
    } catch {
      showToast('设置失败');
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-title">{brand ? `${brand.name} · 款式管理` : '款式管理'}</div>
        <button className="btn btn-primary" onClick={openCreate}>+ 新建款式</button>
      </div>

      {loading ? (
        <TableSkeleton rows={5} cols={5} />
      ) : models.length === 0 ? (
        <EmptyState title="还没有款式，开始创建吧" action={
          <button className="btn btn-primary" onClick={openCreate}>+ 新建款式</button>
        } />
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>编码</th>
                <th>展示名</th>
                <th>批发价</th>
                <th>角标</th>
                <th>在售色数</th>
                <th style={{ textAlign: 'right' }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {models.map((model) => (
                <tr key={model.id}>
                  <td className="mono">{model.code}</td>
                  <td>{model.name}</td>
                  <td>
                    {editingPriceId === model.id ? (
                      <div className="price-edit">
                        <input
                          type="number"
                          value={editingPrice}
                          onChange={(e) => setEditingPrice(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') submitPrice(model.id); }}
                          onBlur={() => submitPrice(model.id)}
                          autoFocus
                        />
                        <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>MMK</span>
                      </div>
                    ) : (
                      <span
                        className="price-display"
                        onClick={() => { setEditingPriceId(model.id); setEditingPrice(String(model.price)); }}
                      >
                        {formatPrice(model.price)} MMK
                      </span>
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {(['hot', 'new', 'limit'] as BadgeType[]).map((type) => (
                        <BadgeChip
                          key={type}
                          type={type}
                          active={model.badge.includes(type)}
                          onToggle={() => toggleBadge(model, type)}
                        />
                      ))}
                    </div>
                  </td>
                  <td>{model.saleColorCount}</td>
                  <td>
                    <div className="actions">
                      <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/brands/${brandId}/models/${model.id}/colors`)}>
                        进入颜色
                      </button>
                      <button className="btn-icon" onClick={() => openEdit(model)} title="编辑">✎</button>
                      <button className="btn-icon danger" onClick={() => setDeleteTarget(model)} title="删除">🗑</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Drawer */}
      {drawerOpen && (
        <Drawer
          title={editId ? '编辑款式' : '新建款式'}
          onClose={() => setDrawerOpen(false)}
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => setDrawerOpen(false)}>取消</button>
              <button className="btn btn-primary" onClick={handleSave}>保存</button>
            </>
          }
        >
          <div className="form-group">
            <label className="form-label">编码 <span className="req">*</span></label>
            <input className="form-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="如 KM-208" />
          </div>
          <div className="form-group">
            <label className="form-label">展示名 <span className="req">*</span></label>
            <input className="form-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="如 托特包" />
          </div>
          <div className="form-group">
            <label className="form-label">批发价 (MMK) <span className="req">*</span></label>
            <input type="number" className="form-input" value={price} onChange={(e) => setPrice(Number(e.target.value))} min={0} />
          </div>
          <div className="form-group">
            <label className="form-label">描述</label>
            <textarea className="form-textarea" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="选填" />
          </div>
          <div className="form-group">
            <label className="form-label">角标</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {(['hot', 'new', 'limit'] as BadgeType[]).map((type) => (
                <BadgeChip
                  key={type}
                  type={type}
                  active={badge.includes(type)}
                  onToggle={() => setBadge((prev) => prev.includes(type) ? prev.filter((b) => b !== type) : [...prev, type])}
                />
              ))}
            </div>
          </div>
        </Drawer>
      )}

      {/* Delete Confirm */}
      {deleteTarget && (
        <ConfirmDialog
          title="删除款式"
          danger
          body={`确定要删除「${deleteTarget.code} · ${deleteTarget.name}」吗？将级联删除其下所有颜色，此操作不可撤销。`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
